import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRfqs } from "@/features/rfqs/api";
import { RfqStatusBadge } from "@/features/rfqs/RfqStatusBadge";
import { useBranch } from "@/context/BranchContext";
import { formatDate } from "@/features/assets/format";
import { ActionGuard } from "@/components/auth/ActionGuard";
import { useModuleAccess } from "@/hooks/useIsReadOnly";


const STATUSES = ["Draft", "Published", "Quotations Received", "Awarded", "Cancelled"];

export const Route = createFileRoute("/_authenticated/rfqs/")({
  component: RfqIndex,
});

function RfqIndex() {
  const navigate = useNavigate();
  const { canWrite } = useModuleAccess("rfqs");
  const [status, setStatus] = useState("all");
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const { data = [], isLoading } = useRfqs(status === "all" ? null : status, effectiveBranchId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">RFQs &amp; Quotations</h1>
          <p className="text-sm text-muted-foreground">Request for Quotation lifecycle: draft → publish → quotations → award.</p>
        </div>
        <ActionGuard can={canWrite}>
          <Button onClick={() => navigate({ to: "/rfqs/new" })}><Plus className="h-4 w-4 mr-1" />New RFQ</Button>
        </ActionGuard>
      </div>

      <Card><CardContent className="p-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
          data.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No RFQs.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>RFQ No</TableHead>
              <TableHead>From Indent</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Published</TableHead>
              <TableHead>Due</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/rfqs/$rfqId", params: { rfqId: r.id } })}>
                  <TableCell className="font-medium">{r.rfq_number}</TableCell>
                  <TableCell className="text-muted-foreground">{r.indent_details?.indent_no ?? "—"}</TableCell>
                  <TableCell>{r.branch_details?.name ?? "—"}</TableCell>
                  <TableCell><RfqStatusBadge status={r.status} /></TableCell>
                  <TableCell>{r.published_at ? formatDate(r.published_at) : "—"}</TableCell>
                  <TableCell>{r.due_date ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
